// Components==============
import { faSmile } from '@fortawesome/pro-solid-svg-icons';
import CircleButton from 'components/UI/CircleButton/CircleButton';
import { AnimatePresence, motion } from 'framer-motion';
import { useRedux } from 'hooks/useRedux';
import { ProjectDetailsContext } from 'pages/Workspace/ProjectDetails/ProjectDetails';
import React, { useContext, useState } from 'react';
import './MultipleEmojis.module.scss';
import EmojiModal from 'components/UI/EmojiModal/EmojiModal';

// =========================

const variants = {
  visible: (index: number) => ({ y: -35 * index, zIndex: index }), // When hovering, spread up on y-axis
  invisible: (index: number) => ({ y: -0.2 * index, zIndex: index }), // When not hovering, stack on z-axis with small offset
};

const variantChild = {
  visible: { opacity: 1 },
  invisible: { opacity: 1 },
};

export default function MultipleEmojis({
  process,
}: {
  process: ProcessProcessEntity;
}) {
  const [hovering, setHovering] = useState<boolean>(false);
  const [emojiModalIsOpen, setEmojiModalIsOpen] = useState(false);

  const { processGroups, handleProcessSave } = useContext(
    ProjectDetailsContext
  );

  // Get all emotions from Redux store
  const allEmotions = useRedux((state) =>
    state.activity?.entities.allIds
      .map((id) => state.activity?.entities.byId[id])
      .filter((a) => a?.type === 'emotion')
  );

  // Map emotion slugs to emotion objects, limit to 2 max
  const emojiObjects =
    process.emotions
      ?.map((slug) =>
        allEmotions?.find((emotion: any) => emotion?.slug === slug)
      )
      .filter((exists) => exists)
      .slice(0, 2) || []; // Limit to max 2 emojis

  const onClick = () => setEmojiModalIsOpen(true);

  return (
    <>
      {emojiObjects.length ? (
        <div styleName="relative">
          <div styleName={`container ${hovering ? 'hovering' : ''}`}>
            <motion.div
              onMouseEnter={() => {
                setHovering(true);
              }}
              onMouseLeave={() => {
                setHovering(false);
              }}
              styleName={`emoji-wrapper ${hovering ? 'hovering' : ''} ${
                emojiObjects.length === 1 ? 'one-emoji ' : 'two-emojis'
              }`}
              onClick={onClick}
            >
              {emojiObjects.map(
                (emoji: any, i) =>
                  !!emoji && (
                    <motion.div
                      key={emoji?.id}
                      styleName="emoji"
                      variants={variants}
                      initial="invisible"
                      animate={hovering ? 'visible' : 'invisible'}
                      custom={emojiObjects.length - i}
                    >
                      <motion.div
                        styleName="emoji-circle"
                        variants={variantChild}
                        initial="invisible"
                        animate={hovering ? 'visible' : 'invisible'}
                      >
                        <img
                          src={emoji.thumbnail}
                          alt={emoji.name}
                          styleName="emoji-image"
                        />
                      </motion.div>
                    </motion.div>
                  )
              )}
              <div styleName="emoji">
                <CircleButton
                  icon={faSmile}
                  color="dark-grey"
                  onClick={onClick}
                />
              </div>
            </motion.div>
          </div>
        </div>
      ) : (
        <CircleButton icon={faSmile} color="dark-grey" onClick={onClick} />
      )}
      <AnimatePresence>
        {emojiModalIsOpen && (
          <EmojiModal
            setModalIsOpen={setEmojiModalIsOpen}
            initialEmotions={process.emotions || []}
            handleSave={(emotions: any) => {
              // Update the process in the processGroups structure
              const updatedProcessGroups = processGroups?.map((group) => ({
                ...group,
                activities: group.activities.map((activity) =>
                  activity.processId === process.id
                    ? {
                        ...activity,
                        emotions: emotions.map((e: any) => e.slug),
                      }
                    : activity
                ),
              }));

              // Pass the updated processGroups to handleProcessSave
              handleProcessSave(updatedProcessGroups);
            }}
          />
        )}
      </AnimatePresence>
    </>
  );
}
